import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { DefaultGhostButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import React from 'react';

import useKioskList from './kiosk-list.controller';

const ID = 'kiosk-list';

export interface KioskListProps<T extends { id: string }> {
    id: string;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams>) => Promise<PaginatedResponse<T>>;
    rowElement: (props: T, index: number, isLast: boolean) => React.ReactNode;
    emptyText?: React.ReactNode;
    /** Page size for pagination. Default 25. */
    pageSize?: number;
    /** Called whenever a batch of items is fetched (initial load and load-more). */
    onDataLoaded?: (items: T[]) => void;
}

export const KioskList = <T extends { id: string }>(props: KioskListProps<T>) => {
    const viewProps = useKioskList<T>(props);

    if (viewProps.loading) {
        return (
            <div className='inline-flex h-full w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                <Spinner />
            </div>
        );
    }

    if (viewProps.error) {
        return (
            <div className='inline-flex h-full w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                <div className='text-status-danger'>{viewProps.error}</div>
            </div>
        );
    }

    return (
        <div
            id={ID + '-' + props.id}
            className='flex w-full flex-col items-start justify-start'>
            {viewProps.data && viewProps.data.length === 0 ? (
                <div className='inline-flex w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                    <span className='text-secondary py-3 text-center text-sm font-medium'>{props.emptyText ?? 'No data'}</span>
                </div>
            ) : (
                <div className='flex w-full flex-col items-start justify-start gap-2'>
                    {viewProps.data && viewProps.data.map((obj, index) => props.rowElement(obj, index, index === viewProps.data!.length - 1))}
                    {viewProps.hasMore && (
                        <div className='flex w-full justify-center py-3'>
                            <DefaultGhostButton
                                text={CommonMessage.LISTS.LOAD_MORE}
                                onClick={viewProps.loadMore}
                                isLoading={viewProps.loadingMore}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KioskList;
